import {
  Badge,
  Box,
  HStack,
  Heading,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {useState} from 'react';
import {useClient} from '@/haligator-factory';
import {useHalState} from '@synako/haligator';
import {useTranslation} from 'react-i18next';
import {BlueButton} from '@/components/Button/BlueButton';

interface ProduitMarcheDebug {
  /**
   * Identifiant du client testé
   */
  client_id: string;
  /**
   * Statut d'affichage des produits marchés pour ce client
   */
  affichage_produit_marche: 'active' | 'desactive';
  /**
   * Informations sur l'article testé (si fourni)
   */
  article_test?: {
    /**
     * Code de l'article testé
     */
    code_article?: string;
    /**
     * L'article existe-t-il dans le catalogue du client
     */
    existe_dans_catalogue?: boolean;
    /**
     * L'article est-il marqué comme produit marché dans Tolede
     */
    produit_marche_tolede?: boolean;
    /**
     * L'article sera-t-il affiché comme produit marché côté client
     */
    sera_affiche?: boolean;
  };
  /**
   * Statistiques générales sur les produits marchés
   */
  statistiques?: {
    /**
     * Nombre total d'articles dans le catalogue
     */
    total_articles_catalogue?: number;
    /**
     * Nombre d'articles marqués comme produit marché
     */
    articles_produit_marche?: number;
    /**
     * Pourcentage d'articles produit marché
     */
    pourcentage_produit_marche?: number;
  };
}

// Route de debug pour les produits marchés
export const DebugProduitMarcheView: React.FC = () => {
  const client = useHalState(useClient());
  const clientData = client.data;
  const {t} = useTranslation();

  // État local pour stocker les données de debug récupérées du back
  const [debugData, setDebugData] = useState<ProduitMarcheDebug | null>(null);
  // Code article saisi par l'utilisateur pour test spécifique
  const [codeArticle, setCodeArticle] = useState('');
  // État de chargement pour les boutons
  const [loading, setLoading] = useState(false);

  // Fonction pour récupérer les données de debug du back
  const fetchDebugData = async (withArticle = false) => {
    if (!clientData?.id) return;

    setLoading(true);

    try {
      // Construction de l'URL avec client_id obligatoire
      const queryParams = new URLSearchParams({client_id: clientData.id});
      // Ajout du code article si demandé et saisi
      if (withArticle && codeArticle) {
        queryParams.append('code_article', codeArticle);
      }
      const url = `/api/debug/produit-marche?${queryParams.toString()}`;

      // Appel à la route de debug (route simple)
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setDebugData(data);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour la couleur des badges de statut
  const getStatutBadgeColor = (statut: 'active' | 'desactive') => {
    switch (statut) {
      case 'active':
        return 'green';
      case 'desactive':
        return 'red';
    }
  };

  // Badge pour affichage Oui/Non avec couleurs
  const getBooleanBadge = (value: boolean) => (
    <Badge
      colorScheme={value ? 'green' : 'red'}
      color={value ? 'gray.dark' : 'white'}
    >
      {value ? 'OUI' : 'NON'}
    </Badge>
  );

  return (
    <VStack
      spacing={6}
      p={8}
      align='stretch'
    >
      <Heading
        as='h1'
        size='lg'
        textAlign='center'
      >
        {t('debug.produit_marche.titre')}
      </Heading>

      {/* Affichage de l'ID du client */}
      <Box>
        <Text
          fontSize='sm'
          color='gray.600'
        >
          {t('debug.produit_marche.client_id')} {clientData?.id}
        </Text>
      </Box>

      {/* Bouton pour charger les informations générales */}
      <HStack spacing={4}>
        <BlueButton
          texte={t('debug.produit_marche.charger_infos')}
          onClick={() => fetchDebugData(false)}
          isLoading={loading}
          size='sm'
        />
      </HStack>

      {/* Section pour tester un article spécifique */}
      <Box
        p={4}
        borderWidth={1}
        borderRadius='md'
      >
        <Heading
          as='h3'
          size='md'
          mb={4}
        >
          {t('debug.produit_marche.tester_un_article')}
        </Heading>
        <HStack spacing={4}>
          <Input
            placeholder='Code article (ex: 123456)'
            value={codeArticle}
            onChange={(e) => {
              setCodeArticle(e.target.value);
            }}
            size='sm'
          />
          <BlueButton
            texte={t('debug.produit_marche.tester_article')}
            onClick={() => fetchDebugData(true)}
            isLoading={loading}
            size='sm'
            isDisabled={!codeArticle.trim()}
          />
        </HStack>
      </Box>

      {/* Affichage des résultats de debug */}
      {debugData && (
        <VStack
          spacing={6}
          align='stretch'
        >
          {/* Section statut d'affichage des produits marchés */}
          <Box
            p={4}
            borderWidth={1}
            borderRadius='md'
          >
            <Heading
              as='h3'
              size='md'
              mb={4}
            >
              {t('debug.produit_marche.statut_affichage_produit_marche_bo')}
            </Heading>
            <HStack>
              <Text>{t('debug.produit_marche.affichage_produit_marche')}</Text>
              {/* Badge avec couleur selon le statut actif/désactivé */}
              <Badge
                colorScheme={getStatutBadgeColor(
                  debugData.affichage_produit_marche,
                )}
              >
                {debugData.affichage_produit_marche.toUpperCase()}
              </Badge>
            </HStack>
          </Box>

          {/* Section statistiques du catalogue */}
          <Box
            p={4}
            borderWidth={1}
            borderRadius='md'
          >
            <Heading
              as='h3'
              size='md'
              mb={4}
            >
              {t('debug.produit_marche.statistiques_catalogue')}
            </Heading>
            <Table size='sm'>
              <Thead>
                <Tr>
                  <Th>{t('debug.produit_marche.metriques')}</Th>
                  <Th>{t('debug.produit_marche.valeur')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>{t('debug.produit_marche.total_articles_catalogue')}</Td>
                  <Td>
                    {debugData.statistiques?.total_articles_catalogue ?? 0}
                  </Td>
                </Tr>
                <Tr>
                  <Td>{t('debug.produit_marche.articles_produit_marche')}</Td>
                  <Td>
                    {debugData.statistiques?.articles_produit_marche ?? 0}
                  </Td>
                </Tr>
                <Tr>
                  <Td>
                    {t('debug.produit_marche.pourcentage_produit_marche')}
                  </Td>
                  <Td>
                    {(
                      debugData.statistiques?.pourcentage_produit_marche ?? 0
                    ).toFixed(2)}
                    %
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          {/* Section résultats du test d'un article spécifique */}
          {debugData.article_test && (
            <Box
              p={4}
              borderWidth={1}
              borderRadius='md'
            >
              <Heading
                as='h3'
                size='md'
                mb={4}
              >
                {t('debug.produit_marche.test_article')}{' '}
                {debugData.article_test.code_article}
              </Heading>

              {/* Tableau des vérifications pour l'article testé */}
              <Table
                size='sm'
                mb={4}
              >
                <Tbody>
                  <Tr>
                    <Td>{t('debug.produit_marche.existe_catalogue')}</Td>
                    <Td>
                      {getBooleanBadge(
                        debugData.article_test.existe_dans_catalogue ?? false,
                      )}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>
                      {t('debug.produit_marche.produit_marche_selon_tolede')}
                    </Td>
                    <Td>
                      {getBooleanBadge(
                        debugData.article_test.produit_marche_tolede ?? false,
                      )}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td>
                      {t('debug.produit_marche.sera_affiche_produit_marche')}
                    </Td>
                    <Td>
                      {getBooleanBadge(
                        debugData.article_test.sera_affiche ?? false,
                      )}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
          )}
        </VStack>
      )}
    </VStack>
  );
};
